// object-handling-browser.ts — binds janus-engine/actions/object-handling-layers.ts (Psion Telekinetic "Mental Grip" /
// "Gravity Sovereign", 2026-10-07) to the page's caches: a creature's passive / feat definitions come from its own blob catalog,
// then the object cache, then the full-nest finder (every cache root's catalog). A definition the page lacks reads as no grant.
import { inventoryField, itemsCol, layerEntriesOf, layerOf } from './array-types';
import { cachedInventoryRow } from '../janus-engine/inventory/cached-inventory-trees';
import { proximityWithinReach } from './proximity';
import { hurlObjectRowRefusal, hurlObjectRule, objectInteractionRangeFt, throwObjectMaxSizeId, type HurlObjectReaders, type ObjectHandlingReaders } from '../janus-engine/actions/object-handling-layers';

type Row = Record<string, unknown>;
type PageWindow = Window & {
    FateTide?: { objectCache?: Map<string, unknown> };
    ftFindFullNestedItem?: (id: string) => unknown;
};
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const full = (value: unknown): Row | null => { const row = record(value); return row && record(row.items) && record(row.item_layer_types) ? row : null; };

function entryId(entry: unknown): string {
    if (typeof entry === 'string') return (entry.split('|')[0] ?? '').trim().toUpperCase();
    const row = record(entry);
    return String(row?.item_layer_effect ?? record(row?.items)?.item_id ?? row?.item_id ?? row?.ability_id ?? '').split('|')[0]!.trim().toUpperCase();
}

export function pageObjectHandlingReaders(creature: Row): ObjectHandlingReaders {
    const w = window as PageWindow;
    return {
        value: (row, key) => row ? layerOf(row, key) ?? null : null,
        entries: (row, key) => row ? layerEntriesOf(row, key) : [],
        definition: entry => {
            const id = entryId(entry);
            if (!UUID.test(id)) return null;
            const catalog = record(creature.catalog);
            const hit = full(catalog?.[id] ?? catalog?.[id.toLowerCase()]);
            if (hit) return hit;
            const cached = full(w.FateTide?.objectCache?.get(id) ?? w.FateTide?.objectCache?.get(id.toLowerCase()));
            if (cached) return cached;
            try { return full(w.ftFindFullNestedItem?.(id) ?? null); } catch { return null; }
        },
    };
}

/** The creature's reach for unattended objects in feet (0 = none). */
export function pageObjectInteractionRangeFt(creature: unknown): number {
    const row = record(creature);
    return row ? objectInteractionRangeFt(row, pageObjectHandlingReaders(row)) : 0;
}

/** Telekinesis reach toward an unattended row (throw-object-gate.ts telekineticReach): the creature's object_interaction_range_ft
 *  and whether the row lies within it — synchronous, over the rows the page already holds (null = geometry unknown). */
export function pageTelekineticReach(creature: unknown, row: unknown): { rangeFt: number; within: boolean | null } {
    const rangeFt = pageObjectInteractionRangeFt(creature);
    if (!(rangeFt > 0)) return { rangeFt: 0, within: null };
    // via unknown: some bundles (trade-trainer-init) declare a narrower global SIZE_TYPES without base_size
    const sizes = (window as unknown as { SIZE_TYPES?: Array<{ item_id: string; base_size?: number }> }).SIZE_TYPES;
    const size = (id: string): number | null => {
        const base = sizes?.find(entry => String(entry.item_id).toUpperCase() === id)?.base_size;
        return typeof base === 'number' && Number.isFinite(base) ? base : null;
    };
    try {
        return { rangeFt, within: proximityWithinReach(creature, row, size, rangeFt) };
    } catch {
        return { rangeFt, within: null };
    }
}

/** The largest SizeType id the creature can throw ('' = none). */
export function pageThrowObjectMaxSizeId(creature: unknown): string {
    const row = record(creature);
    return row ? throwObjectMaxSizeId(row, pageObjectHandlingReaders(row)) : '';
}

// ── HURLED OBJECT pick (2026-10-08, Psion Telekinetic hurl mentas) ──────────────────────────────────────────────────────────────────────
// The object a hurl ability will fly, chosen BEFORE its targeting (object-card/hurl-object.ts: the object's wheel → Telekinesis → the
// menta). Kept on window, not in this module: this file is bundled into several bundles and the pick must be ONE value for all of them.
export interface HurlPick { casterId: string; abilityId: string; inventoryId: string; atMs: number }
/** A pick older than this is stale (the player wandered off or cancelled the targeting); the cast then asks for a fresh one. Kept short
 *  so a cancelled pick does not linger into an unrelated later cast (Codex review 2026-10-08). A dispatched cast spends it at once. */
export const HURL_PICK_TTL_MS = 45_000;
// A standalone view cast via unknown (never intersected with Window): some bundles (trade-trainer-init) declare a narrower global
// SIZE_TYPES without base_size, and other globals differ per bundle (coordinator fix, 2026-10-08 — the pageTelekineticReach pattern).
type HurlWindow = { __ftHurlPick?: HurlPick | null; SIZE_TYPES?: Array<{ item_id: string; base_size?: number }>;
    FateTide?: { encounterCache?: Map<string, unknown> }; ftResolveCachedInventoryRow?: (id: string) => unknown };
const hw = (): HurlWindow => window as unknown as HurlWindow;

export function pageSetHurlPick(pick: Omit<HurlPick, 'atMs'>): void {
    hw().__ftHurlPick = { casterId: pick.casterId.toUpperCase(), abilityId: pick.abilityId.toUpperCase(), inventoryId: pick.inventoryId.toUpperCase(), atMs: Date.now() };
}
/** The fresh pick for this caster + ability, or null. */
export function pageHurlPick(casterId: string, abilityId: string): HurlPick | null {
    const pick = hw().__ftHurlPick;
    if (!pick || Date.now() - pick.atMs > HURL_PICK_TTL_MS) return null;
    return pick.casterId === casterId.toUpperCase() && pick.abilityId === abilityId.toUpperCase() ? pick : null;
}
export function pageClearHurlPick(): void { hw().__ftHurlPick = null; }

/** The page's richest copy of a held inventory instance, including sparse container and encounter trees. */
export function pageInventoryRow(inventoryId: string, encounterId = ''): Row | null {
    const id = inventoryId.toUpperCase();
    try { const rendered = record(hw().ftResolveCachedInventoryRow?.(id)); if (rendered) return rendered; } catch { /* fall through */ }
    const cache = hw().FateTide?.encounterCache;
    return cachedInventoryRow(cache?.get(encounterId.toUpperCase()) ?? cache?.get(encounterId), id);
}
/** Every row lying loose on this encounter's floor that the page holds. */
export function pageFloorRows(encounterId: string): Row[] {
    const cache = hw().FateTide?.encounterCache;
    const enc = record(cache?.get(encounterId.toUpperCase()) ?? cache?.get(encounterId));
    const rows = Array.isArray(enc?.encounter_inventories_arr) ? enc!.encounter_inventories_arr as unknown[] : [];
    return rows.map(row => record(row)).filter((row): row is Row => !!row);
}
function hurlReaders(): HurlObjectReaders {
    const sizes = hw().SIZE_TYPES;
    const size = (id: string): number | null => {
        const base = sizes?.find(entry => String(entry.item_id).toUpperCase() === id)?.base_size;
        return typeof base === 'number' && Number.isFinite(base) ? base : null;
    };
    return {
        value: (row, key) => row ? layerOf(row, key) ?? null : null,
        inventoryField: (row, key) => row ? inventoryField(row, key) ?? null : null,
        itemField: (row, key) => row ? itemsCol(row, key) ?? null : null,
        itemSizeId: row => inventoryField(row, 'inventory_size') || layerOf(row, 'size'),
        within: (caster, row, rangeFt) => { try { return proximityWithinReach(caster, row, size, rangeFt); } catch { return null; } },
    };
}
const casterIds = (caster: Row): { casterId: string; encounterId: string } => {
    const ec = record(caster.encounter_creatures);
    const one = (v: unknown): string => String(Array.isArray(v) ? v[0] : v ?? '').trim().toUpperCase();
    return { casterId: one(ec?.encounter_creature_id), encounterId: one(ec?.encounter_id) };
};
/** Why this caster cannot hurl this row with this ability (null = it can). */
export function pageHurlRowRefusal(caster: Row, ability: Row, row: Row | null): string | null {
    return hurlObjectRowRefusal({ caster, ability, row, encounterId: casterIds(caster).encounterId }, hurlReaders());
}
/** The loose floor rows this ability could hurl right now. */
export function pageHurlCandidates(caster: Row, ability: Row): Row[] {
    const { encounterId } = casterIds(caster);
    return pageFloorRows(encounterId).filter(row => {
        const id = String(inventoryField(row, 'inventory_id') ?? '');
        return pageHurlRowRefusal(caster, ability, pageInventoryRow(id, encounterId) ?? row) === null;
    });
}
/** The cast-time gate (ability-preconditions.ts hurlObjectRefusal): a hurl ability needs a fresh, still-eligible pick. */
export function pageHurlObjectRefusal(caster: Row, ability: Row): string | null {
    const rule = hurlObjectRule(ability, { value: (row, key) => row ? layerOf(row, key) ?? null : null });
    if (!rule) return null;
    const { casterId, encounterId } = casterIds(caster);
    const abilityId = String(itemsCol(ability, 'item_id') ?? '');
    const name = String(itemsCol(ability, 'item_name') ?? 'this ability');
    const pick = pageHurlPick(casterId, abilityId);
    if (!pick) {
        const scope = `${rule.capLabel === 'Titanic' ? 'an' : `a ${rule.capLabel} or smaller`} unattended object within ${rule.rangeFt} ft`;
        return pageHurlCandidates(caster, ability).length === 0
            ? `${name} needs ${scope} to hurl — there is none.`
            : `${name} needs ${scope}: choose it first (open the object's wheel → Telekinesis → ${name}).`;
    }
    return pageHurlRowRefusal(caster, ability, pageInventoryRow(pick.inventoryId, encounterId));
}
